/**
 * EL MEZCLADOR DE AUDIO DEL SHARER (docs/SHARE-SCREEN-SPEC.md §4.10): al peer viaja UN solo track
 * de audio = mezcla de (a) el micrófono, siempre pedido, y (b) el audio del sistema/pestaña cuando
 * `getDisplayMedia` lo devolvió. La mezcla se hace con `AudioContext` → `createMediaStreamSource`
 * (uno por fuente) → `createMediaStreamDestination`, y el mic pasa por un `GainNode` que el Mute
 * baja a 0.
 *
 * La DECISIÓN (`decideMix`) está separada del armado (`buildMixer`) para probarla en vitest/node:
 *  - mic + sistema → mezcla (no hay otra forma de mandar los dos en un track).
 *  - solo mic en Safari → mic DIRECTO: F0 todavía no verificó que Safari negocie un track salido de
 *    `createMediaStreamDestination`, y un mic crudo se sabe que sí.
 *  - solo mic en el resto → mezcla igual, para que el Mute tenga el mismo camino (gain 0 + enabled).
 *  - solo sistema (mic negado) → directo: no hay nada que mezclar ni que silenciar.
 *
 * Mute = `micTrack.enabled = false` + gain 0. NUNCA se para el track: pararlo obliga a renegociar y
 * el viewer vería un corte en vez de silencio.
 *
 * El `AudioContext` lo crea quien llama, DENTRO del gesto del clic (capture.ts): creado tras un
 * `await` puede nacer `suspended` y el viewer oiría silencio sin ningún error a la vista.
 */

export type MixPlan = 'none' | 'mic-direct' | 'system-direct' | 'mix';

/** Safari de verdad (no Chrome/Edge/Opera/Firefox en macOS o iOS, que también dicen "Safari/"). */
export function isSafari(ua: string): boolean {
  return /Safari\//.test(ua) && !/Chrome\/|Chromium\/|Edg\/|OPR\/|CriOS\/|FxiOS\/|Firefox\//.test(ua);
}

/** Firefox de escritorio o móvil. */
export function isFirefox(ua: string): boolean {
  return /Firefox\/|FxiOS\//.test(ua);
}

/**
 * ¿Este navegador PUEDE devolver audio del sistema/pestaña en `getDisplayMedia`? Solo Chromium
 * (Chrome, Edge, Opera). Firefox y Safari lo ignoran sin error: el chip del sharer tiene que decir
 * "no disponible en {browser}" y no "comparte la pantalla completa".
 */
export function systemAudioSupported(ua: string): boolean {
  return !isSafari(ua) && !isFirefox(ua);
}

export function decideMix(i: { hasMic: boolean; hasSystem: boolean; safari: boolean }): MixPlan {
  if (!i.hasMic && !i.hasSystem) return 'none';
  if (i.hasMic && i.hasSystem) return 'mix';
  if (i.hasSystem) return 'system-direct';
  return i.safari ? 'mic-direct' : 'mix';
}

export type Mixer = {
  /** El track que va al peer (null si no hay ninguna fuente de audio). */
  track: MediaStreamTrack | null;
  /** Lo que REALMENTE se armó (si el AudioContext falló, la mezcla cae a directo). */
  plan: MixPlan;
  /**
   * El AudioContext de la mezcla (null si el audio va directo o no hay contexto): el provider guarda
   * esta referencia para re-`resume()`arlo en un gesto posterior si quedó suspendido.
   */
  ctx: AudioContext | null;
  /**
   * ¿El contexto de la mezcla está suspendido AHORA? Un `createMediaStreamDestination` de un contexto
   * suspendido escribe SILENCIO digital en el track saliente (el que mira no oye nada y NO salta ningún
   * error): es el bug de prod 2026-09-08. false cuando va directo (el mic crudo siempre lleva señal).
   */
  isSuspended(): boolean;
  /** Reintenta `resume()` (típicamente desde un gesto del usuario). Devuelve true si quedó 'running'. */
  resume(): Promise<boolean>;
  setMuted(muted: boolean): void;
  isMuted(): boolean;
  /** Para el track mezclado y cierra el AudioContext. Las fuentes las para quien las pidió. */
  close(): void;
};

/** Techo del respiro que se le da al `resume()` del contexto (ver buildMixer): breve para no colgar. */
const RESUME_GRACE_MS = 300;

/** Espera acotada; se usa para no dejar que `resume()` bloquee la aptitud del track sin límite. */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => { setTimeout(resolve, ms); });
}

/**
 * Arma el track según `decideMix`. `ctx` puede ser null (navegador sin AudioContext o falló al
 * crearlo): entonces todo va directo. Si la mezcla se pide pero el contexto no la puede armar, se
 * cae al mic (o al sistema) crudo, nunca a silencio.
 *
 * Es async porque, si el contexto NO está 'running', le da un respiro ACOTADO al `resume()` (nunca un
 * `await` sin techo: un contexto suspendido puede dejar esa promesa PENDIENTE hasta una activación
 * nueva —autoplay de Web Audio— y colgaría captureScreen() → startShare entero, no solo el audio). Si
 * el resume no alcanza (sin gesto válido), el track igual queda armado y el provider lo revive en el
 * próximo gesto con `resume()`: el MISMO track vuelve a llevar señal al correr el contexto, sin
 * renegociar. `isSuspended()` lee `ctx.state`, así que refleja la verdad aunque el resume no resolviera.
 */
export async function buildMixer(ctx: AudioContext | null, mic: MediaStream | null, system: MediaStreamTrack | null, ua: string): Promise<Mixer> {
  const micTrack = mic?.getAudioTracks()[0] ?? null;
  const wanted = decideMix({ hasMic: !!micTrack, hasSystem: !!system, safari: isSafari(ua) });

  let gain: GainNode | null = null;
  let track: MediaStreamTrack | null = null;
  let usedCtx: AudioContext | null = null;
  let muted = false;

  if (wanted === 'mix' && ctx) {
    try {
      const dest = ctx.createMediaStreamDestination();
      if (micTrack) {
        gain = ctx.createGain();
        gain.gain.value = 1;
        ctx.createMediaStreamSource(new MediaStream([micTrack])).connect(gain).connect(dest);
      }
      if (system) ctx.createMediaStreamSource(new MediaStream([system])).connect(dest);
      track = dest.stream.getAudioTracks()[0] ?? null;
      if (track) {
        usedCtx = ctx;
        // Se dispara el resume dentro del gesto del clic, pero con techo: un `await ctx.resume()` sin
        // límite puede no retornar NUNCA si el navegador deja la promesa pendiente (contexto suspendido,
        // autoplay aún no permitido) y eso colgaría el share entero, no solo el audio. Se espera como
        // mucho `RESUME_GRACE_MS` y se sigue: si quedó suspendido, la red de seguridad del provider lo
        // reanuda en el próximo gesto (revive el MISMO track). `isSuspended()` lee ctx.state.
        if (ctx.state !== 'running') { await Promise.race([ctx.resume().catch(() => {}), sleep(RESUME_GRACE_MS)]); }
      }
    } catch {
      gain = null;
      track = null;
    }
  }
  if (!track) {
    // Directo: Safari sin sistema, sin AudioContext, o el mezclador no pudo. El contexto sobrante se
    // cierra ya (cada AudioContext abierto es un hilo de audio vivo).
    track = micTrack ?? system;
    if (ctx) void ctx.close().catch(() => {});
  }
  const plan: MixPlan = usedCtx ? 'mix' : !track ? 'none' : micTrack ? 'mic-direct' : 'system-direct';

  return {
    track,
    plan,
    ctx: usedCtx,
    isSuspended: () => !!usedCtx && usedCtx.state === 'suspended',
    async resume() {
      const c = usedCtx;
      if (!c) return true; // directo: no hay contexto que reanudar; el track crudo ya lleva señal
      if ((c.state as string) === 'running') return true;
      try { await c.resume(); } catch { /* el próximo gesto lo reintenta */ }
      return (c.state as string) === 'running';
    },
    setMuted(m: boolean) {
      muted = m;
      // Las dos puertas a la vez: el track del mic en silencio Y la ganancia en 0. El sistema sigue.
      if (micTrack) micTrack.enabled = !m;
      if (gain) gain.gain.value = m ? 0 : 1;
    },
    isMuted: () => muted,
    close() {
      if (usedCtx) {
        if (track) { try { track.stop(); } catch { /* ya parado */ } }
        void usedCtx.close().catch(() => {});
        usedCtx = null;
      }
    },
  };
}
